import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import GlobalVariable from '../../GlobalVariable';
import api, {isUnauthorizedError, redirectToLogin} from '../../api';
import Menu from '../Menu';
import PageHeader from '../PageHeader';
import './Salary.scss';

type Company = {id: number; title?: string; address?: string};
type Category = {key: string; title: string; revenue: number; salary: number; operations: number};
type Day = {date: string; revenue: number; salaryBase: number | null; tax: number | null; salary: number; topUp: number; operations: number; categories: Category[]};
type Operation = {id: number | null; recordId: number | null; time: string; category: string; title: string; amount: number; paid: number; discount: number; ownerCoverage: number | null; masterCoverage: number | null; masterRevenue: number | null; tax: number | null; salaryBase: number | null; salaryMode: string | null; salaryRate: number | null; salary: number};
type DayDetails = {date: string; showTax: boolean; operations: Operation[]};
type SalaryData = {
    month: string;
    company: Company;
    companies: Company[];
    visible: boolean;
    showTax: boolean;
    summary: {revenue: number; salaryBase: number | null; tax: number | null; salary: number; topUp: number; operations: number; coefficient: number; minSalaryPerShift: number};
    days: Day[];
};

const currentMonth = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const moveMonth = (month: string, offset: number) => {
    const [year, number] = month.split('-').map(Number);
    const date = new Date(year, number - 1 + offset, 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (month: string) => {
    const [year, number] = month.split('-').map(Number);
    return new Intl.DateTimeFormat('ru-RU', {month: 'long', year: 'numeric'}).format(new Date(year, number - 1, 1));
};

const money = (amount: number | null | undefined) => Number(amount || 0).toLocaleString('ru-RU', {maximumFractionDigits: 2}) + ' ₽';
const dayLabel = (date: string) => date.slice(8, 10) + '.' + date.slice(5, 7);
const operationKind = (category: string) => category === 'service' ? 'Услуга' : category === 'consum' ? 'Расходник' : 'Товар';
const operationCount = (count: number) => {
    const lastTwo = count % 100;
    const last = count % 10;
    const word = lastTwo >= 11 && lastTwo <= 14 ? 'операций'
        : last === 1 ? 'операция' : last >= 2 && last <= 4 ? 'операции' : 'операций';
    return `${count} ${word}`;
};
const salaryMonthFromUrl = () => {
    const value = new URLSearchParams(window.location.search).get('month');
    return value && /^\d{4}-(0[1-9]|1[0-2])$/.test(value) && value <= currentMonth() ? value : currentMonth();
};
const salaryCompanyFromUrl = () => {
    const value = Number(new URLSearchParams(window.location.search).get('companyId'));
    return Number.isSafeInteger(value) && value > 0 ? value : null;
};

function OperationCard({operation, day, company, showTax, coefficient}: {operation: Operation; day: Day; company: Company; showTax: boolean; coefficient: number}) {
    return <details className='salary-operation'>
        <summary>
            <span className='salary-operation-main'><small>{operation.time} · {operation.recordId ? `Визит №${operation.recordId} · ` : ''}{operationKind(operation.category)} · операция №{operation.id}</small><strong>{operation.title}</strong></span>
            <span className='salary-operation-amount'><small>{money(operation.amount)}</small><strong>+ {money(operation.salary)}</strong></span>
        </summary>
        <div className='salary-operation-detail'>
            <div className='salary-line'><span>Стоимость</span><strong>{money(operation.amount)}</strong></div>
            <div className='salary-line'><span>Оплачено деньгами</span><strong>{money(operation.paid)}</strong></div>
            {operation.discount > 0 && <div className='salary-line'><span>Скидки и баллы</span><strong>{money(operation.discount)}</strong></div>}
            {showTax && Number(operation.ownerCoverage) > 0 && <div className='salary-line'><span>Из скидок покрывает салон</span><strong>{money(operation.ownerCoverage)}</strong></div>}
            {showTax && Number(operation.masterCoverage) > 0 && <div className='salary-line'><span>Из скидок покрывает мастер</span><strong>{money(operation.masterCoverage)}</strong></div>}
            {showTax && <div className='salary-line'><span>База до налога</span><strong>{money(operation.masterRevenue)}</strong></div>}
            {showTax && <div className='salary-line'><span>НДС / налог</span><strong>− {money(operation.tax)}</strong></div>}
            {showTax && <div className='salary-line'><span>База начисления</span><strong>{money(operation.salaryBase)}</strong></div>}
            {operation.salaryRate !== null && <div className='salary-line'><span>Ставка</span><strong>{operation.salaryMode === 'fixed' ? money(operation.salaryRate) : `${operation.salaryRate.toLocaleString('ru-RU')}%`}</strong></div>}
            {!showTax && <p className='salary-note mb-0'>НДС и детали базы скрыты настройками салона.</p>}
            {coefficient !== 1 && <div className='salary-line'><span>Коэффициент мастера</span><strong>× {coefficient.toLocaleString('ru-RU')}</strong></div>}
            <div className='salary-line salary-line-total'><span>Начислено</span><strong>{money(operation.salary)}</strong></div>
            <Link className='salary-dispute-link' to={{pathname: '/hr-feedback', state: {salaryDispute: {
                companyId: company.id, companyAddress: company.address || company.title || '', date: day.date,
                operationId: operation.id, title: operation.title, salary: operation.salary,
            }}}}>Сообщить об ошибке в начислении</Link>
        </div>
    </details>;
}

function SalaryDay({day, company, showTax, coefficient}: {day: Day; company: Company; showTax: boolean; coefficient: number}) {
    const [open, setOpen] = useState(false);
    const [details, setDetails] = useState<DayDetails | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [retryKey, setRetryKey] = useState(0);

    useEffect(() => {
        if (!open || details || error) return;
        let active = true;
        setLoading(true);
        api({
            url: GlobalVariable.host() + '/widget/staff/v2/salary/day',
            method: 'GET',
            params: {date: day.date, companyId: company.id},
            withCredentials: true,
        }).then(result => {
            if (!active) return;
            if (!result.data || !Array.isArray(result.data.operations)) {
                throw new Error('invalid_salary_day_response');
            }
            setDetails(result.data);
            setLoading(false);
        }).catch(reason => {
            if (!active) return;
            if (isUnauthorizedError(reason)) {
                redirectToLogin();
                return;
            }
            setError(reason?.response?.status === 403
                ? 'Доступ к расчёту изменился. Обновите страницу.'
                : 'Не удалось загрузить операции за день.');
            setLoading(false);
        });
        return () => { active = false; };
    }, [open, day.date, company.id, details, error, retryKey]);

    const retry = () => { setError(''); setRetryKey(value => value + 1); };
    const groups: {key: string; recordId: number | null; time: string; operations: Operation[]}[] = [];
    const groupByKey = new Map<string, typeof groups[number]>();
    (details?.operations || []).forEach((operation, index) => {
        const key = operation.recordId ? `visit-${operation.recordId}` : `operation-${operation.id}-${index}`;
        let group = groupByKey.get(key);
        if (!group) {
            group = {key, recordId: operation.recordId, time: operation.time, operations: []};
            groupByKey.set(key, group);
            groups.push(group);
        }
        group.operations.push(operation);
    });
    return <details className='chio-card salary-day p-3 mb-3' onToggle={event => setOpen((event.currentTarget as HTMLDetailsElement).open)}>
        <summary>
            <span><strong>{dayLabel(day.date)}</strong><small>{operationCount(day.operations)}</small></span>
            <strong>{money(day.salary)}</strong>
        </summary>
        <div className='salary-day-detail'>
            <div className='salary-line'><span>Стоимость операций</span><strong>{money(day.revenue)}</strong></div>
            {showTax && <div className='salary-line'><span>НДС / налог</span><strong>− {money(day.tax)}</strong></div>}
            {showTax && <div className='salary-line'><span>База начисления</span><strong>{money(day.salaryBase)}</strong></div>}
            {day.categories.filter(category => category.operations > 0).map(category =>
                <div className='salary-line' key={category.key}><span>{category.title}</span><strong>{money(category.salary)}</strong></div>
            )}
            {day.topUp > 0 && <div className='salary-line'><span>Доплата до минимума</span><strong>+ {money(day.topUp)}</strong></div>}
            <div className='salary-line salary-line-total'><span>Зарплата за день</span><strong>{money(day.salary)}</strong></div>
        </div>
        <div className='salary-operations'>
            <h3>Операции в расчёте</h3>
            {loading && <p className='salary-note' role='status'>Загружаем операции…</p>}
            {error && <div className='salary-detail-error' role='alert'>{error} <button type='button' onClick={retry}>Повторить</button></div>}
            {details && details.operations.length === 0 && <p className='salary-note'>Операций за день нет.</p>}
            {details && groups.map(group => group.operations.length > 1
                ? <details className='salary-visit' key={group.key}>
                    <summary><span>{group.time} · Визит №{group.recordId} · {operationCount(group.operations.length)}</span><strong>+ {money(group.operations.reduce((sum, operation) => sum + operation.salary, 0))}</strong></summary>
                    <div className='salary-visit-operations'>{group.operations.map((operation, index) =>
                        <OperationCard key={operation.id || index} operation={operation} day={day} company={company} showTax={details.showTax} coefficient={coefficient}/>
                    )}</div>
                </details>
                : <OperationCard key={group.key} operation={group.operations[0]} day={day} company={company} showTax={details.showTax} coefficient={coefficient}/>
            )}
            {details && details.operations.length > 0 && <p className='salary-note mt-2 mb-0'>Доплата до минимума показана отдельно. Суммы операций могут отличаться от итога на копейки из-за округления.</p>}
        </div>
    </details>;
}

export default function Salary() {
    const [month, setMonth] = useState(salaryMonthFromUrl);
    const [companyId, setCompanyId] = useState<number | null>(salaryCompanyFromUrl);
    const [data, setData] = useState<SalaryData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        api({
            url: GlobalVariable.host() + '/widget/staff/v2/salary',
            method: 'GET',
            params: {month, ...(companyId !== null ? {companyId} : {})},
            withCredentials: true,
        }).then(result => {
            if (active) {
                setData(result.data);
                setLoading(false);
            }
        }).catch(reason => {
            if (!active) return;
            if (isUnauthorizedError(reason)) {
                redirectToLogin();
                return;
            }
            setData(null);
            setError('Не удалось загрузить расчёт. Попробуйте ещё раз.');
            setLoading(false);
        });
        return () => { active = false; };
    }, [month, companyId, reloadKey]);

    const changeMonth = (offset: number) => setMonth(value => moveMonth(value, offset));
    const canGoForward = month < currentMonth();

    return (
        <div className='main salary-page'>
            <div className='content mx-w-content'>
                <PageHeader pageTitle='Расчёт зарплаты' backBtnUrl='/'/>
                <div className='pt-header'/>

                <div className='salary-month chio-card ml-3 mr-3 mt-3 mb-3 p-3'>
                    <button type='button' onClick={() => changeMonth(-1)} aria-label='Предыдущий месяц'>‹</button>
                    <strong>{monthLabel(month)}</strong>
                    <button type='button' onClick={() => changeMonth(1)} disabled={!canGoForward} aria-label='Следующий месяц'>›</button>
                </div>

                {data && data.companies.length > 1 && (
                    <label className='salary-company chio-card ml-3 mr-3 mb-3 p-3'>
                        <span>Салон</span>
                        <select value={data.company.id} onChange={event => setCompanyId(Number(event.target.value))}>
                            {data.companies.map(company => <option key={company.id} value={company.id}>{company.address || company.title}</option>)}
                        </select>
                    </label>
                )}

                {loading && <div className='chio-card ml-3 mr-3 mb-3 p-3' role='status'>
                    {data && !data.visible ? 'Проверяем доступ…' : 'Загружаем расчёт…'}
                </div>}
                {error && <div className='chio-card ml-3 mr-3 mb-3 p-3' role='alert'>{error}</div>}
                {!loading && data && !data.visible &&
                    <section className='salary-hidden chio-card ml-3 mr-3 mb-3 p-3' aria-labelledby='salary-hidden-title'>
                        <span className='salary-hidden-icon' aria-hidden='true'>₽</span>
                        <h2 id='salary-hidden-title'>Расчёт пока не открыт</h2>
                        <p>Чтобы здесь появился расчёт, попросите управляющего или владельца салона включить «Показывать зарплату мастеру» в настройках зарплаты. Затем проверьте доступ.</p>
                        <div className='salary-hidden-note'>С вашим аккаунтом всё в порядке. Доступ к расчёту задаётся отдельно для каждого салона.</div>
                        <button type='button' className='salary-hidden-retry' onClick={() => setReloadKey(value => value + 1)}>
                            Проверить доступ
                        </button>
                    </section>}

                {!loading && data && data.visible && <>
                    <section className='salary-total chio-card ml-3 mr-3 mb-3 p-3'>
                        <div className='salary-eyebrow'>Начислено за {monthLabel(month)}</div>
                        <div className='salary-total-value'>{money(data.summary.salary)}</div>
                        <div className='salary-note'>По настройкам зарплаты салона{data.company.address ? ` · ${data.company.address}` : ''}</div>
                    </section>

                    <section className='chio-card ml-3 mr-3 mb-3 p-3' aria-label='Состав расчёта'>
                        <h2 className='txt-large bold mb-3'>Состав расчёта</h2>
                        <div className='salary-line'><span>Стоимость операций</span><strong>{money(data.summary.revenue)}</strong></div>
                        {data.showTax && <div className='salary-line'><span>НДС / налог</span><strong>− {money(data.summary.tax)}</strong></div>}
                        {data.showTax && <div className='salary-line'><span>База начисления после НДС</span><strong>{money(data.summary.salaryBase)}</strong></div>}
                        {data.summary.topUp > 0 && <div className='salary-line'><span>Доплата до минимума</span><strong>+ {money(data.summary.topUp)}</strong></div>}
                        {data.summary.coefficient !== 1 && <div className='salary-line'><span>Коэффициент мастера</span><strong>× {data.summary.coefficient.toLocaleString('ru-RU')}</strong></div>}
                        <div className='salary-line salary-line-total'><span>Зарплата</span><strong>{money(data.summary.salary)}</strong></div>
                        {!data.showTax && <p className='salary-note mb-0 mt-2'>НДС учтён в расчёте, его сумма скрыта настройками салона.</p>}
                        <p className='salary-note mb-0 mt-2'>База учитывает скидки и баллы. Показаны оплаченные услуги и товары; сумма может измениться после корректировок в CRM.</p>
                    </section>

                    <section className='salary-days ml-3 mr-3' aria-label='Расчёт по дням'>
                        <h2 className='txt-large bold mb-3'>По дням</h2>
                        {data.days.length === 0 && <div className='chio-card p-3 mb-3'>За этот месяц пока нет данных для расчёта.</div>}
                        {data.days.map(day => <SalaryDay key={`${data.company.id}-${day.date}`} day={day} company={data.company} showTax={data.showTax} coefficient={data.summary.coefficient}/>)}
                    </section>
                </>}
            </div>
            <Menu/>
        </div>
    );
}
